import { Box, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  DotProps,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styles from '../Dashboard.module.scss';
import dayjs from 'dayjs';
import { IAlertStatic } from '~/utils/interface/dashboard';
import dashboardApi from '~/apis/dashboard';
const cx = classNames.bind(styles);

const LabeledDot = (props: DotProps & { value?: number }) => {
  const { cx, cy, value } = props;
  if (cx == null || cy == null) return null;
  const displayValue = Array.isArray(value) ? value[1] : value;
  return (
    <g>
      <circle cx={cx} cy={cy} r={10} fill='#ED1002' opacity={0.25} />
      <circle cx={cx} cy={cy} r={4} fill='#ff4d4f' stroke='#fff' strokeWidth={2} />
      <text x={cx} y={cy - 12} textAnchor='middle' fontSize={12} fill='#666'>
        {displayValue}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label, hourLabel, warningLabel }: any) =>
  active && payload?.length ? (
    <div
      style={{
        background: '#fff',
        border: '1px solid #eee',
        boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
        borderRadius: 8,
        padding: '8px 10px',
        fontSize: 12,
      }}
    >
      <div>
        <b>{hourLabel}:</b> {`${dayjs(label).format('HH:mm')} - ${dayjs(label).add(1, 'hour').format('HH:mm')}`}
      </div>
      <div>
        <b>{warningLabel}:</b> {payload[0].value}
      </div>
      <div style={{ color: '#999' }}>{dayjs(label).format('DD/MM/YYYY')}</div>
    </div>
  ) : null;

export default function DailyWarning() {
  const { t } = useTranslation('admin/home');
  const [data, setData] = useState<IAlertStatic[]>([]);
  const maxValue = Math.max(...data.map((d) => d.Count));
  const step = 4;
  const maxTick = Math.ceil(maxValue / step) * step || 1;
  const ticks = Array.from({ length: maxTick / step + 1 }, (_, i) => i * step);

  useEffect(() => {
    (async () => {
      try {
        const response = await dashboardApi.getAlertStatistic24h();
        setData(response.data);
      } catch (error) {}
    })();
  }, []);

  const CustomizedAxisTick = (props: any) => {
    const label = dayjs(props.payload.value).format('HH');
    const offset = props.width / data.length / 2;
    return (
      <g transform={`translate(${props.x - offset},${props.y})`}>
        <text x={0} y={0} dy={10} textAnchor='middle' fill='#666'>
          {label}
        </text>
      </g>
    );
  };

  return (
    <Box className={cx('w-[55%] h-full', 'dashboard-widget')}>
      <Typography className={cx('dashboard-widget-title', 'dashboard-widget-title-text')}>
        {t('dailyWarning')}
      </Typography>
      <Box className={cx('dashboard-widget-body')}>
        <ResponsiveContainer width='100%' height='100%'>
          <AreaChart data={data} margin={{ top: 32, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid horizontal={false} vertical={true} stroke='#f0f0f0' strokeDasharray='0' />
            <YAxis
              domain={[0, maxTick]}
              ticks={ticks}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#eee' }}
            />

            {Array.from({ length: maxTick / step + 1 }, (_, i) => i * step).map((y) => (
              <ReferenceLine key={y} y={y} stroke='#f0f0f0' strokeDasharray='3 3' />
            ))}
            <XAxis
              dataKey='Hour'
              interval={0}
              tickLine={false}
              tick={<CustomizedAxisTick />}
              padding={{ left: 30, right: 30 }}
            />
            <Tooltip
              content={
                <CustomTooltip
                  hourLabel={t('dailyWarningComponent.hour')}
                  warningLabel={t('dailyWarningComponent.warning')}
                />
              }
              cursor={false}
            />
            <ReferenceArea x1='01' x2='03' y1={0} y2='auto' fill='#ff4d4f' fillOpacity={0.06} />
            <defs>
              <linearGradient id='alertFill' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor='#ff4d4f' stopOpacity={0.7} />
                <stop offset='100%' stopColor='#ff4d4f' stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <Area
              type='linear'
              dataKey='Count'
              stroke='#ff4d4f'
              strokeWidth={2}
              fill='url(#alertFill)'
              activeDot={false}
              dot={<LabeledDot />}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
